import React, { useEffect, useState } from "react";
import MainComponent from "./main/main";
import { Page } from "./types/page";
import { ApiEndpoint, apiService } from "./lib/api";
import shadow from "react-shadow";
import styles from "./app.css?inline";
import { cn } from "./lib/utils";
import { Button } from "./components/ui/button";
import { X } from "lucide-react";
import { renderIntegrations } from "./integrations";
import { PageView } from "./types/enum";

type WidgetOptions = {
  color: {
    primary: string;
    secondary: string;
  };
  questions: Record<string, string>;
  hideEvent: boolean;
};

interface DayscheduleWidgetProps extends React.HTMLAttributes<HTMLElement> {
  url?: string;
  options?: string;
  popup?: boolean;
  onClose?: () => void;
}

// --- Tailwind normalizer for ShadowRoot ---
function normalizeTailwind(css: string): string {
  return css
    .replace(/:root\b/g, ":host")
    .replaceAll("((-webkit-hyphens:none)) and ", "")
    .replaceAll("(-webkit-hyphens: none) and ", "");
}

export const DayscheduleWidget = ({
  url,
  options,
  popup,
  onClose,
}: DayscheduleWidgetProps) => {
  const [config, setConfig] = useState<WidgetOptions | null>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colorMode, setColorMode] = useState<string | null>("system");
  const [colorTheme, setColorTheme] = useState<string | null>("theme-default");

  // handle color mode / theme when page loads
  useEffect(() => {
    if (!page?.color) return;

    if (page.color.mode === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      setColorMode(systemTheme);
    } else {
      setColorMode(page.color.mode || "system");
    }

    if (page.color.theme?.endsWith("-scaled")) {
      setColorTheme(`theme-${page.color.theme} theme-scaled`);
    } else {
      setColorTheme(`theme-${page.color.theme || "default"}`);
    }
  }, [page?.color]);

  // parse options JSON
  useEffect(() => {
    if (options) {
      try {
        setConfig(JSON.parse(options));
      } catch (e) {
        console.error("Invalid options JSON", e);
      }
    }
  }, [options]);

  // resolve resource after page + url
  const resource = React.useMemo(() => {
    if (!page || !url) return null;
    const parsedUrl = new URL(url);
    if (parsedUrl.pathname.length > 2) {
      return page.resources.find((x: any) => x.slug === parsedUrl.pathname);
    }
    return null;
  }, [page, url]);

  // fetch page data
  const fetchPage = async (subdomain: string | null) => {
    if (!subdomain) {
      setError("Invalid page url");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiService.get(ApiEndpoint.pages, subdomain);
      if (!data) {
        setError("Page not found");
      } else {
        setPage(data);
      }
    } catch (err) {
      setError("Failed to load page");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // run fetch on url change
  useEffect(() => {
    if (url) {
      const parsedUrl = new URL(url);
      const hostParts = parsedUrl.hostname.split(".");
      const subdomain = hostParts.length > 2 ? hostParts[0] : null;
      fetchPage(subdomain);
    } else {
      setError("No URL provided");
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (page?.integrations) {
      renderIntegrations(page.integrations);
    }
  }, [page?.integrations]);

  return (
    <shadow.div>
      <style>{normalizeTailwind(styles)}</style>

      {/* LOADING VIEW */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      )}

      {/* ERROR VIEW */}
      {!loading && error && (
        <div className="fixed inset-0 flex items-center justify-center text-red-600">
          <p>{error}</p>
        </div>
      )}

      {/* MAIN VIEW */}
      {!loading && page && (
        <>
          <div
            className={cn(
              "ds-widget max-w-[1280px] mx-auto rounded-md",
              colorMode
            )}
          >
            <div className={cn(colorTheme)}>
              {popup && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 z-50 rounded-full"
                  onClick={onClose}
                >
                  <X className="h-5 w-5" />
                </Button>
              )}
              <MainComponent
                page={page}
                view={resource ? (resource.type as PageView) : "resources"}
                resource={resource}
                queryParams={config?.questions}
              />
            </div>
          </div>
        </>
      )}
    </shadow.div>
  );
};
